import type { FAQType } from '@kanbu/schema';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';

import { TablePage } from '@/components/page/TablePage';
import { useFAQsCols } from '@/features/faqs/useFAQsCols';
import { useDetailRowHandler } from '@/hooks/useDetailRowHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { trpc, trpcClient } from '@/services/trpc';

export const Route = createFileRoute('/_baseLayout/chats/$chatId/faqs/')({
  component: FaqsPage,
  loader: async ({ context: { trpcUtils }, params: { chatId } }) =>
    trpcUtils.chats.findOne.ensureData({ id: chatId }),
});

export function FaqsPage() {
  const { chatId } = Route.useParams();
  const { t } = useLingui();
  const columns = useFAQsCols();
  const [data] = trpc.chats.findOne.useSuspenseQuery({ id: chatId });

  const handleQuery = useTableQuery(trpcClient.faqs.findAll.query, {
    populate: ['parent'],
    where: {
      chat: chatId,
    },
  });

  const handleRow = useDetailRowHandler<FAQType>(row => ({
    to: '/chats/$chatId/faqs/$faqId/edit',
    params: {
      chatId,
      faqId: row.original.id,
    },
  }));

  return (
    <TablePage
      title={t`FAQs`}
      breadcrumbs={[
        {
          label: data.name,
          to: '/chats/$chatId/edit',
          params: { chatId },
        },
        {
          label: t`FAQs`,
          to: '/chats/$chatId/faqs',
          params: { chatId },
        },
      ]}
      createButton={{
        to: '/chats/$chatId/faqs/create',
        params: { chatId },
        label: t`Create FAQ`,
      }}
      columns={columns}
      queryKey={[...getQueryKey(trpc.faqs.findAll), { chatId }]}
      onQueryFn={handleQuery}
      onRow={handleRow}
    />
  );
}
